import { useMemo, useState } from 'react'
import { Merge } from 'lucide-react'
import {
  useAllCreatedProducts,
  useCatalogEntriesByIds,
  usePoolPreview,
  usePoolProducts,
} from '@/hooks/useProducts'
import type { CreatedProduct, ProductCatalogEntry } from '@/types/products'
import { inHandProducts } from '@/lib/productSupersession'
import { canPoolWith, poolRejectionReason, pooledVolume, type PoolCandidate } from '@/lib/productPool'
import { PoolLimsPreview } from '@/components/procedure/PoolLimsPreview'
import { LimsInputsNotFound } from '@/components/procedure/LimsInputsNotFound'
import { previewGate } from '@/lib/limsInputs'
import { blockingWarnings, parseBufferMl } from '@/lib/poolPreview'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  POOL_REASONS,
  ReasonField,
  composeReason,
  reasonIsComplete,
} from '@/components/procedure/ReasonField'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

/**
 * Pool / Combine Product — merge several bags into one new product.
 *
 * The inverse of Split, and deliberately NOT symmetric with it: split
 * children keep the parent's DIN because they are still that donation,
 * whereas pooled material came from several donations, so no source DIN can
 * describe it honestly. The pooled bag is therefore a brand-new product with
 * a DIN the server mints, and runs the ordinary Create flow afterwards — label,
 * then storage.
 *
 * The picker only offers bags that pass the safety rule (same patient, same
 * product class) and says why the others are excluded rather than hiding
 * them, so an operator who expected a bag to be selectable learns the reason.
 * The server re-checks the same rule: this is convenience, not the control.
 */
export function PoolProductDialog({
  open,
  onClose,
  submissionId,
  formDefinitionId,
  procedureId,
  patientMrn,
  onPooled,
}: {
  open: boolean
  onClose: () => void
  submissionId: string
  formDefinitionId: string
  procedureId: string
  patientMrn: string
  onPooled: (
    pooled: CreatedProduct,
    sources: CreatedProduct[],
    catalogEntry: ProductCatalogEntry | null,
  ) => void
}) {
  const poolProducts = usePoolProducts()
  // Only while open: this dialog is always mounted on the case page (z8nrz7e1c2).
  const allProducts = useAllCreatedProducts(open)
  // The class of every candidate's code, resolved by id: the pooling safety
  // rule compares classes, and the paged ISBT catalog cannot be scanned for them.
  const catalog = useCatalogEntriesByIds(allProducts.data?.map(p => p.catalogEntryId) ?? [])
  // Both must land before the list shows: the catalog lookup waits on the
  // product list, and without classes every bag would briefly read as
  // unpoolable. A write while closed only marks the list stale, so a refetch
  // counts too.
  const productsLoading =
    allProducts.isLoading ||
    (allProducts.isFetching && allProducts.isStale) ||
    catalog.isLoading

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Free text, not a number: an empty box must mean "none", not 0. */
  const [bufferMl, setBufferMl] = useState('')
  // The blocking warnings the operator ticked through, as one key — see `blockingKey`.
  const [acknowledgedKey, setAcknowledgedKey] = useState<string | null>(null)

  function reset() {
    setSelectedIds([])
    setReason('')
    setReasonDetail('')
    setBufferMl('')
    setAcknowledgedKey(null)
    setError(null)
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      reset()
      onClose()
    }
  }

  const classById = useMemo(() => {
    const map = new Map<string, string>()
    for (const entry of catalog.byId.values()) map.set(entry.id, entry.productClass)
    return map
  }, [catalog.data])

  // Superseded products are history, not bags (z8nrz7e72t); nor is an
  // original already divided by a split (z8nrz7g2wm).
  const candidates = inHandProducts(allProducts.data ?? []).filter(p => p.patientMrn === patientMrn)
  const asCandidate = (p: CreatedProduct): PoolCandidate => ({
    id: p.id,
    patientMrn: p.patientMrn,
    productClass: classById.get(p.catalogEntryId) ?? '',
  })

  const selected = candidates.filter(p => selectedIds.includes(p.id))
  const selectedCandidates = selected.map(asCandidate)

  const { value: bufferValue, error: bufferError } = parseBufferMl(bufferMl)
  // Display only. The volume the pooled product is actually created with is
  // computed on the server from the same members, so this cannot disagree with
  // what gets stored — it is the denominator of every pooled concentration.
  const volume = pooledVolume(selected.map(p => p.volumeMl)) + (bufferValue ?? 0)

  // What pooling this selection would produce, worked out by the server. These
  // are dosing figures: a preview computed here would be a second
  // implementation of the formulas, free to disagree with what the pool stores.
  const preview = usePoolPreview(
    {
      sourceProductIds: selected.map(p => p.id),
      bufferMl: bufferValue,
      submissionId,
      procedureId,
    },
    // Not asked while the buffer is invalid: a preview without it would be a
    // preview of a different pool.
    open && selected.length >= 2 && !bufferError,
  )

  // Everything pooled shares a class by the rule above, so the pooled product
  // is created from the first source's catalog entry.
  const catalogEntry =
    (selected[0] ? catalog.byId.get(selected[0].catalogEntryId) : null) ?? null

  // The server refuses a pool when any member's lab values cannot be found;
  // the preview reports the same list, per member.
  const inputsNotFound = selected.length >= 2 ? (preview.data?.inputsNotFound ?? []) : []
  // The operator pools what the preview showed them, so the preview has to
  // have answered for this selection and buffer first.
  const gate = previewGate(preview)
  // Warnings that void the pooled dose (all members' cells summing to zero, no
  // member volume) are stated outside the collapsed panel and must be
  // acknowledged. The acknowledgement is tied to the exact warnings it read, so
  // a different selection producing new ones asks again.
  const blocking = selected.length >= 2 ? blockingWarnings(preview.data?.warnings ?? []) : []
  const blockingKey = blocking.map(w => w.code + w.message).join('|')
  const blockingAcknowledged = blocking.length === 0 || acknowledgedKey === blockingKey
  const canPool =
    selected.length >= 2 &&
    !!catalogEntry &&
    !bufferError &&
    gate.ready &&
    inputsNotFound.length === 0 &&
    blockingAcknowledged &&
    reasonIsComplete(reason, reasonDetail)

  function toggle(p: CreatedProduct) {
    setSelectedIds(prev =>
      prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id],
    )
  }

  async function handlePool() {
    if (!canPool || !catalogEntry) return
    setSaving(true)
    setError(null)
    try {
      // A pooled bag gets a DIN of our own, minted by the server as for Create.
      const pooled = await poolProducts.mutateAsync({
        sourceProductIds: selected.map(p => p.id),
        catalogEntryId: catalogEntry.id,
        submissionId,
        formDefinitionId,
        procedureId,
        reason: composeReason(reason, reasonDetail),
        bufferMl: bufferValue,
      })
      const sources = selected
      handleOpenChange(false)
      onPooled(pooled, sources, catalogEntry)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to pool products.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Pool / combine products</DialogTitle>
          <DialogDescription>
            Select two or more bags to combine into one new product. Only bags from the same
            patient and the same product class can be pooled.
          </DialogDescription>
        </DialogHeader>

        <div className='max-h-72 overflow-y-auto rounded-lg border'>
          {productsLoading ? (
            <p className='p-4 text-sm text-muted-foreground'>Loading products…</p>
          ) : candidates.length === 0 ? (
            <p className='p-4 text-sm text-muted-foreground'>
              No products found for this patient.
            </p>
          ) : (
            candidates.map(p => {
              const rejection = poolRejectionReason(selectedCandidates, asCandidate(p))
              const checked = selectedIds.includes(p.id)
              // "Already selected" is not a rejection to show — it IS the selection.
              const blocked = !checked && !canPoolWith(selectedCandidates, asCandidate(p))
              return (
                <label
                  key={p.id}
                  className={cn(
                    'flex items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0',
                    blocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-accent',
                  )}
                >
                  <Checkbox
                    checked={checked}
                    disabled={blocked}
                    onCheckedChange={() => toggle(p)}
                    aria-label={`Select ${p.din}`}
                  />
                  <span className='font-mono'>{p.din}</span>
                  <span className='text-muted-foreground'>{p.productDescriptionCode || '—'}</span>
                  <span className='ml-auto text-xs text-muted-foreground'>
                    {p.volumeMl != null ? `${p.volumeMl} mL` : 'volume not recorded'}
                    {blocked && rejection ? ` · ${rejection}` : ''}
                  </span>
                </label>
              )
            })
          )}
        </div>

        {selected.length > 0 && (
          <div className='space-y-2 rounded-lg border bg-muted/20 p-3 text-sm'>
            <p className='font-medium'>
              Combining {selected.length} bag{selected.length === 1 ? '' : 's'} into one new
              product
            </p>
            <div className='flex flex-wrap items-center gap-4 text-muted-foreground'>
              <span>
                Combined volume: <span className='font-medium text-foreground'>{volume} mL</span>
              </span>
              <span>
                Product code:{' '}
                <span className='font-mono text-foreground'>
                  {catalogEntry?.productDescriptionCode || '—'}
                </span>
              </span>
              <span>A new DIN is minted when you pool.</span>
            </div>
            <div className='flex items-center gap-2 pt-1'>
              <Label htmlFor='pool-buffer' className='text-xs font-normal text-muted-foreground'>
                Buffer added (mL)
              </Label>
              <Input
                id='pool-buffer'
                type='number'
                min='0'
                step='any'
                inputMode='decimal'
                value={bufferMl}
                onChange={e => setBufferMl(e.target.value)}
                placeholder='0'
                className='h-8 w-28'
              />
              <span className='text-xs text-muted-foreground'>
                Dilutes every pooled concentration — leave blank for none.
              </span>
            </div>
            {bufferError && <p className='text-xs text-destructive'>{bufferError}</p>}
          </div>
        )}

        <LimsInputsNotFound missing={inputsNotFound} />

        {blocking.length > 0 && (
          <div role='alert' className='space-y-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm'>
            {blocking.map(w => (
              <p key={w.code} className='text-destructive'>
                {w.message}
              </p>
            ))}
            <label className='flex items-center gap-2 text-xs'>
              <Checkbox
                checked={acknowledgedKey === blockingKey}
                onCheckedChange={v => setAcknowledgedKey(v === true ? blockingKey : null)}
                aria-label='Acknowledge the pooling warnings'
              />
              I have read this and want to pool anyway.
            </label>
          </div>
        )}

        {selected.length >= 2 && !bufferError && !gate.ready && gate.message && (
          <p className={preview.isError ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
            {gate.message}
          </p>
        )}

        {selected.length >= 2 && (
          <PoolLimsPreview
            preview={preview.data}
            isLoading={preview.isLoading}
            error={preview.error as Error | null}
          />
        )}

        {selected.length === 1 && (
          <p className='text-sm text-muted-foreground'>Select at least one more bag to pool.</p>
        )}
        {selected.length >= 2 && (
          <ReasonField
            presets={POOL_REASONS}
            reason={reason}
            detail={reasonDetail}
            onReasonChange={setReason}
            onDetailChange={setReasonDetail}
            hint='Recorded against the pooled product in the audit trail.'
          />
        )}

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <DialogFooter>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handlePool} disabled={!canPool || saving}>
            <Merge className='mr-2 h-4 w-4' />
            {saving ? 'Pooling…' : 'Pool Products'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
